import * as React from 'react';
import { StyleSheet, Text, TouchableOpacity, View, Image } from 'react-native';
import { useHistory } from 'react-router';
import { Auth } from 'aws-amplify';

import { localeTexts } from '../../Locales';
import color from '../../style/color';
import { font } from '../../style/text';
import Button from '../../components/common/Button';
import TextField from '../../components/common/TextField';
import { toggleDevStackMode } from '../../lib/common/devStackMode';
import { usePrevious } from '../../lib/common/utilHooks';
import AuthPageLayout from './AuthPageLayout';

const eyeIcon = { uri: '/assets/images/icons/eye.svg' };
const eyeSlashIcon = { uri: '/assets/images/icons/eye-slash.svg' };

const texts = localeTexts.auth.signIn;

export default function SignInPage() {
    const history = useHistory();
    const [signIn, isLoading, error, clearError] = useSignIn();
    const [email, setEmail] = React.useState<string>('');
    const [password, setPassword] = React.useState<string>('');
    const previousEmail = usePrevious(email);
    const previousPassword = usePrevious(password);
    React.useEffect(() => {
        if (email.length !== previousEmail?.length || password.length !== previousPassword?.length) clearError();
    }, [email, password, previousEmail, previousPassword, clearError]);
    const disabled = !email || !password;
    const onPress = () => signIn(email, password);
    return (
        <AuthPageLayout>
            <Text style={styles.textTitle}>{texts.title}</Text>
            <View style={styles.containerField}>
                <EmailInput {...{ email, setEmail, isLoading }} hasError={error !== undefined} />
            </View>
            <View style={styles.containerField}>
                <PasswordInput
                    {...{ password, setPassword, isLoading, onPress }}
                    hasError={error !== undefined}
                    errorMessage={error !== undefined ? getErrorMessage(error) : undefined}
                />
            </View>
            {/* <TouchableOpacity onPress={() => history.push(`/auth/reset-password?email=${encodeURIComponent(email)}`)}>
                <Text style={styles.textResetPasswordButton}>{texts.resetPassword}</Text>
            </TouchableOpacity> */}
            <Button
                {...{ isLoading, onPress, disabled }}
                useBlackColor={true}
                textStyle={styles.textButton}
                style={styles.containerSignInButton}
                height={40}>
                {texts.button}
            </Button>
            <View style={styles.containerSignUp}>
                <Text style={styles.textSignUp}>{texts.signUp.text + ' '}</Text>
                <TouchableOpacity onPress={() => history.push('/auth/sign-up')}>
                    <Text style={styles.textSignUpButton}>{texts.signUp.button}</Text>
                </TouchableOpacity>
            </View>
        </AuthPageLayout>
    );
}

enum SignInWithEmailError {
    EmailAddressUnknown = 'EmailAddressUnknown',
    Other = 'Other',
}

const useSignIn: () => [
    (email: string, password: string) => void,
    boolean,
    SignInWithEmailError | undefined,
    () => void
] = () => {
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<SignInWithEmailError | undefined>();
    const signIn = async (email: string, password: string) => {
        if (email === 'switchstack') {
            toggleDevStackMode();
            window.location.reload();
        } else if (email.length > 0 && password.length > 0) {
            if (!isLoading) {
                setIsLoading(true);
                try {
                    await Auth.signIn(email, password);
                    window.location.reload();
                } catch (error: any) {
                    if (error?.code === 'NotAuthorizedException') setError(SignInWithEmailError.EmailAddressUnknown);
                    else {
                        console.log(error);
                        setError(SignInWithEmailError.Other);
                    }
                    setIsLoading(false);
                }
            }
        }
    };
    const clearError = () => setError(undefined);
    return [signIn, isLoading, error, clearError];
};

const EmailInput = ({
    email,
    setEmail,
    isLoading,
    hasError,
}: {
    email: string;
    setEmail: (email: string) => void;
    isLoading: boolean;
    hasError?: boolean;
}) => (
    <TextField
        placeholder={texts.email}
        autoCorrect={false}
        autoCapitalize={'none'}
        keyboardType={'email-address'}
        returnKeyType="next"
        autoFocus={true}
        editable={!isLoading}
        onChangeText={setEmail}
        {...{ hasError }}
    />
);

const PasswordInput = ({
    password,
    setPassword,
    isLoading,
    onPress,
    hasError,
    errorMessage,
}: {
    password: string;
    setPassword: (password: string) => void;
    isLoading: boolean;
    onPress: () => void;
    hasError?: boolean;
    errorMessage?: string;
}) => {
    const [hidePassword, setHidePassword] = React.useState<boolean>(true);
    return (
        <TextField
            placeholder={texts.password}
            autoCorrect={false}
            autoCapitalize={'none'}
            secureTextEntry={hidePassword}
            returnKeyType="go"
            editable={!isLoading}
            onChangeText={setPassword}
            onSubmitEditing={onPress}
            {...{ hasError, errorMessage }}>
            <TouchableOpacity onPress={() => setHidePassword(!hidePassword)} activeOpacity={0.8}>
                {hidePassword ? (
                    <Image source={eyeIcon} style={styles.imageEyeIcon} />
                ) : (
                    <Image source={eyeSlashIcon} style={styles.imageEyeIcon} />
                )}
            </TouchableOpacity>
        </TextField>
    );
};

// The message sits under the fields as design-system supporting text rather than in a banner
function getErrorMessage(error: SignInWithEmailError): string {
    if (error === SignInWithEmailError.EmailAddressUnknown) return texts.error.wrongCredentials;
    return texts.error.default;
}

const styles = StyleSheet.create({
    containerSignUp: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 24,
    },
    containerField: {
        marginBottom: 16,
    },
    // Same gap between the last field and the CTA as on the sign-up page
    containerSignInButton: {
        marginTop: 16,
    },
    textTitle: {
        fontFamily: font.wallopSansSemiBold,
        fontSize: 20,
        color: color.black,
        marginBottom: 24,
    },
    textResetPasswordButton: {
        textAlign: 'center',
        fontFamily: font.instrumentSansSemiBold,
        fontSize: 14,
        color: color.black,
        marginBottom: 24,
    },
    textButton: {
        fontFamily: font.instrumentSansMedium,
        fontSize: 14,
    },
    textSignUp: {
        textAlign: 'center',
        fontFamily: font.instrumentSansMedium,
        fontSize: 14,
        color: color.black,
    },
    // Link style aligned with the user-web-app (app.joko.com) design system: black, semibold, underlined
    textSignUpButton: {
        textAlign: 'center',
        fontFamily: font.instrumentSansSemiBold,
        fontSize: 14,
        color: color.black,
        borderBottomWidth: 1,
        borderBottomColor: color.black,
    },
    // Design-system eye / eye-slash glyphs at `icons.medium` (24px)
    imageEyeIcon: {
        width: 24,
        height: 24,
        resizeMode: 'contain',
    },
});
